import { DefaultMap } from '../../defaultMap';
import { BadInputError } from '@watershed/errors/BadInputError';
import assertNever from '@watershed/shared-util/src/assertNever';
import {
  ASTCall,
  ASTExpr,
  ASTProgram,
  expressionsEqual,
  stringifyExpr,
} from '../ast';
import { getFunction } from '../functions';
import { isMacro } from '../functions/helpers/macros';
import { extractStringLiteral } from '../literal';

export type Reference = {
  // Name is an arbitrary name given to this (distinct) reference data lookup
  name: string;
  // The first argument to ref(...)
  slug: string;
  // The optional third argument to ref(...)
  monthExpr?: ASTExpr;
  // The matching predicates
  kwargs: ASTCall['kwargs'];
};

// Returns a list of distinct References in a given program (i.e., the return
// value is logically a set). Each Reference can be thought of as a `ref(...)`
// function without its second argument (i.e., without a column to look up), and
// has been assigned a unique name. The name is arbitrary, and is subject to
// change over time. Callers wishing to depend on the names returned by this
// function MUST save the return value of this function.
//
// Two reference data lookups are the same if their expressions, aside from the
// second argument (the column to return), are equal according to the
// expressionsEqual function.
export function gatherReferences(prog: ASTProgram): Array<Reference> {
  const references = new Array<Reference>();

  for (const line of prog) {
    gatherReferencesExpr(line.expr, references);
  }

  return references;
}

export function gatherReferencesExpr(
  expr: ASTExpr,
  acc?: Array<Reference>
): Array<Reference> {
  // Logically, a Set<Reference> with a custom equality function,
  // referencesEqual
  acc ??= [];

  for (const ref of generateRefs(expr)) {
    const candidate = exprToReference(ref, `__join${acc.length + 1}`);
    // The process of maintaining the references "set" is much more
    // inefficient than it needs to be: worst case it's quadratic in the
    // number of reference data lookups, and generally speaking does a bunch
    // of unnecessary work. Oh well! Hopefully you aren't reading this comment
    // in The Future™ wondering why this code is so slow.
    let found = false;
    for (const reference of acc) {
      if (referencesEqual(reference, candidate)) {
        found = true;
        break;
      }
    }
    if (!found) {
      acc.push(candidate);
    }
  }

  return acc;
}

// Given an expression, yield all calls to `ref(...)` anywhere they occur,
// *even* in hidden use cases like `ref?(...)` and `forex(...)`.
//
// For now, we make the assumption that every hidden use can be macro-expanded
// to make the calls to `ref(...)` known to us. This just so happens to be true
// today, because every function that has a hidden lookup can be expressed, *in
// the LScript language*, using `ref(...)` directly. For instance, `ref?(...)`
// is exactly equivalent to `iferror(ref(...), null)`. This might not always be
// true! If/when this assumption changes, we'll have to update this code too.
//
// This function is an interleaved combination of expandMacros and callMeMaybe.
// We used to just call these two functions in order, but that doesn't produce
// correct results when a macro is used as an argument to a ref(...) call, like:
//
//     ref("a", "b", x=ref?("c", "d"))
//
// For the purposes of gathering references, we want two references:
//
//     ref("a", "b", x=ref?("c", "d"))
//     ref("c", "d")
//
// But if we fully expanded all macros first, we'd end up with:
//
//     ref("a", "b", x=iferror(ref("c", "d"), null))
//     ref("c", "d")
function* generateRefs(expr: ASTExpr): Generator<ASTCall> {
  switch (expr.t) {
    case 'boolean':
    case 'number':
    case 'string':
    case 'identifier':
    case 'null':
      return;
    case 'unary':
      yield* generateRefs(expr.expr);
      return;
    case 'binary':
    case 'comparison':
      yield* generateRefs(expr.l);
      yield* generateRefs(expr.r);
      return;
    case 'call':
      if (expr.name === 'ref') {
        yield expr;
      }
      const func = getFunction(expr.name);
      if (func == null) {
        throw new BadInputError(`Unknown function ${expr.name}`);
      }
      if (isMacro(func)) {
        yield* generateRefs(func.expand(expr));
        return;
      }
      for (const arg of expr.args) {
        yield* generateRefs(arg);
      }
      for (const { expr: arg } of expr.kwargs.values()) {
        yield* generateRefs(arg);
      }
      return;
    default:
      assertNever(expr);
  }
}

function referencesEqual(a: Reference, b: Omit<Reference, 'name'>): boolean {
  return expressionsEqual(referenceToExpr(a), referenceToExpr(b));
}

export function referenceToExpr(reference: Omit<Reference, 'name'>): ASTCall {
  const args: Array<ASTExpr> = [
    { t: 'string', v: reference.slug },
    { t: 'string', v: `"<dummy column>"` },
  ];
  if (reference.monthExpr) {
    args.push(reference.monthExpr);
  }

  return {
    t: 'call',
    name: 'ref',
    args,
    kwargs: reference.kwargs,
  };
}

function exprToReference(ref: ASTCall, name: string): Reference {
  if (ref.name !== 'ref') {
    throw new BadInputError(`${stringifyExpr(ref)} was not a ref(...) call`);
  }
  return {
    name,
    slug: extractStringLiteral(ref.args[0]),
    monthExpr: ref.args[2],
    kwargs: ref.kwargs,
  };
}

export function findReference(
  references: Array<Reference>,
  ref: ASTCall
): Reference {
  const candidate = exprToReference(ref, 'dummy');
  for (const reference of references) {
    if (referencesEqual(reference, candidate)) {
      return reference;
    }
  }
  throw new BadInputError(
    `Function ${stringifyExpr(ref)} was not found in references list`
  );
}

// Given a LScript program, return a map from the Reference slugs used in that
// program to the columns needed from a dataset with that slug. This includes
// any columns extracted from that reference, as well as any columns that are
// part of filter predicates (including `start_date` and `end_date` if a time
// filter is applied).
export function slugsToColumns(program: ASTProgram): Map<string, Set<string>> {
  const out = new DefaultMap<string, Set<string>>(() => new Set());
  for (const line of program) {
    slugsToColumnsExpr(line.expr, out);
  }
  return out.toMap();
}

// See docs above. Acts on a single expression instead of an entire program.
// Mutates the `acc` (accumulator) argument in-place.
export function slugsToColumnsExpr(
  expr: ASTExpr,
  acc: DefaultMap<string, Set<string>>
): void {
  for (const ref of generateRefs(expr)) {
    const slug = extractStringLiteral(ref.args[0]);
    const cols = acc.get(slug);

    const col = extractStringLiteral(ref.args[1]);
    cols.add(col);
    if (ref.args.length > 2) {
      cols.add('start_date');
      cols.add('end_date');
    }
    for (const arg of ref.kwargs.keys()) {
      cols.add(arg);
    }
  }
}
